import React from 'react'
import { Routes, Route } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from 'react-query'

// Context Providers
import { AuthProvider } from '@contexts/AuthContext'
import { TenantProvider } from '@contexts/TenantContext'

// Route Components
import MarketingRoutes from '@routes/MarketingRoutes'
import SuperAdminRoutes from '@routes/SuperAdminRoutes'
import PublicRoutes from '@routes/PublicRoutes'
import StateOfficialRoutes from '@routes/StateOfficialRoutes'
import ClientAdminRoutes from '@routes/ClientAdminRoutes'
import ReportingApp from '@pages/reporting/ReportingApp'
import UserRoutes from '@routes/UserRoutes'

// Import debug components
import TestRoute from './components/TestRoute'
import SimpleBearersPage from './pages/public/SimpleBearersPage'
import CompleteBearersPage from './pages/public/CompleteBearersPage'

import AffiliatesPage from './pages/public/AffiliatesPage'
import BaseballRulesPage from './pages/public/BaseballRulesPage'
import ScoreKeepingPage from './pages/public/ScoreKeepingPage'

import BaseballLingoPage from './pages/public/BaseballLingoPage'
import AbbreviationPage from './pages/public/AbbreviationPage'
import Stats101Page from './pages/public/Stats101Page'
import SkillsPage from './pages/public/SkillsPage'
import BattingOrderPage from './pages/public/BattingOrderPage'
import FieldingPositionPage from './pages/public/FieldingPositionPage'
import WhyPlayPage from './pages/public/WhyPlayPage'
import StaticEventsPage from './pages/public/StaticEventsPage'
import ForthcomingEventsPage from './pages/public/ForthcomingEventsPage'
import CalendarEventsPage from './pages/public/CalendarEventsPage'
import CommitteesPage from './pages/public/CommitteesPage'
import AccountsPage from './pages/public/AccountsPage'
import RTIPage from './pages/public/RTIPage'
import WebsiteInfoPage from './pages/public/WebsiteInfoPage'
import StaticGalleryPage from './pages/public/StaticGalleryPage'
import ContactUsPage from './pages/public/ContactUsPage'
import UserLoginPage from './pages/public/UserLoginPage'
import CertificateVerifyPage from './pages/public/CertificateVerifyPage'
import AdminLoginPage from './pages/admin/AdminLoginPage'

// Utils
import { determineAppType } from '@utils/appDetection'

// Create QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

function App() {
  const appType = determineAppType()
  
  // Debug info
  console.log('App Type:', appType);
  console.log('Current URL:', window.location.href);
  console.log('Hostname:', window.location.hostname);
  console.log('Port:', window.location.port);

  const renderRoutes = () => {
    switch (appType) {
      case 'marketing':
        return (
          <Routes>
            <Route path="/bearer" element={<CompleteBearersPage />} />
            <Route path="/affiliates" element={<AffiliatesPage />} />
            <Route path="/rules" element={<BaseballRulesPage />} />
            <Route path="/score-keeping" element={<ScoreKeepingPage />} />
            <Route path="/baseball-lingo" element={<BaseballLingoPage />} />
            <Route path="/abbreviation" element={<AbbreviationPage />} />
            <Route path="/stats" element={<Stats101Page />} />
            <Route path="/skills" element={<SkillsPage />} />
            <Route path="/batting-order" element={<BattingOrderPage />} />
            <Route path="/fielding-position" element={<FieldingPositionPage />} />
            <Route path="/why-play" element={<WhyPlayPage />} />
            <Route path="/events" element={<StaticEventsPage />} />
            <Route path="/forthcoming-events" element={<ForthcomingEventsPage />} />
            <Route path="/calendar-events" element={<CalendarEventsPage />} />
            <Route path="/committees" element={<CommitteesPage />} />
            <Route path="/accounts" element={<AccountsPage />} />
            <Route path="/rti" element={<RTIPage />} />
            <Route path="/website-info" element={<WebsiteInfoPage />} />
            <Route path="/photo-gallery" element={<StaticGalleryPage />} />
            {/* /contact falls through to MarketingRoutes, which serves the
                demo page in the site's own layout. */}
            <Route path="/user-login" element={<UserLoginPage />} />
            <Route path="/certificate-verify" element={<CertificateVerifyPage />} />
            <Route path="/admin-login" element={<AdminLoginPage />} />
            <Route path="/*" element={<MarketingRoutes />} />
          </Routes>
        )
      case 'super-admin':
        return <SuperAdminRoutes />
      case 'tenant':
        return (
          <TenantProvider>
            <Routes>
              <Route path="/admin/*" element={<ClientAdminRoutes />} />
              {/* Results desk and screen mode, outside the portal chrome. */}
              <Route path="/board/*" element={<ReportingApp />} />
              <Route path="/user/*" element={<UserRoutes />} />
              <Route path="/state-official/*" element={<StateOfficialRoutes />} />
              <Route path="/*" element={<PublicRoutes />} />
            </Routes>
          </TenantProvider>
        )
      default:
        return <MarketingRoutes />
    }
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <div className="app">
          {renderRoutes()}
        </div>
      </AuthProvider>
    </QueryClientProvider>
  )
}

export default App